import { ReactNode } from 'react';
import useAuthStore from '../../stores/authStore';
import { USER_ROLE } from '../../types/auth';
import { SuperAdminHeaderActions, ProviderHeaderActions } from './header-actions';

interface HeaderProps {
	onMenuToggle: () => void;
	isMobileSidebarOpen: boolean;
	headerActions?: ReactNode;
}

const Header = ({ onMenuToggle, isMobileSidebarOpen, headerActions }: HeaderProps) => {
	const { user } = useAuthStore();

	// Determine which header actions to render based on role
	const renderHeaderActions = () => {
		if (headerActions) {
			return headerActions;
		}

		if (!user) return null;

		switch (user.role) {
			case USER_ROLE.SUPER_ADMIN:
				return <SuperAdminHeaderActions />;
			case USER_ROLE.SALON_OWNER:
				return <ProviderHeaderActions />;
			default:
				return null;
		}
	};

	return (
		<header
			className="text-white px-4 sm:px-5 md:px-[30px] sticky top-0 z-50 shadow-md flex-shrink-0"
			style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
		>
			<div className="mx-auto flex justify-between items-center py-4 md:py-5 gap-3 md:gap-5">
				{/* Left Section - Hamburger + Logo */}
				<div className="flex items-center gap-2 sm:gap-4">
					{/* Hamburger Menu - Mobile Only */}
					<button
						onClick={onMenuToggle}
						className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 transition-colors cursor-pointer"
						aria-label="Toggle menu"
					>
						{isMobileSidebarOpen ? (
							<svg
								className="w-6 h-6"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						) : (
							<svg
								className="w-6 h-6"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M4 6h16M4 12h16M4 18h16"
								/>
							</svg>
						)}
					</button>

					{/* Logo Section */}
					<div className="w-10 h-10 sm:w-[50px] sm:h-[50px] bg-white rounded-xl flex items-center justify-center text-2xl sm:text-3xl flex-shrink-0">
						💇
					</div>
					<div className="min-w-0">
						<h1 className="text-sm sm:text-xl md:text-2xl font-bold mb-0.5 truncate">
							Beauty Business Portal
						</h1>
						<p className="text-xs sm:text-sm opacity-90 hidden xs:block">
							200Below Network
						</p>
					</div>
				</div>

				{/* Right Section - Role-based Header Actions */}
				{renderHeaderActions()}
			</div>
		</header>
	);
};

export default Header;
